import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Checkout() {
  const { items, subtotal, clear, hydrated } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [newAddr, setNewAddr] = useState({
    full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: ""
  });
  const [payment, setPayment] = useState("phonepe");
  const [submitting, setSubmitting] = useState(false);
  const [pincodeCheck, setPincodeCheck] = useState({ status: "idle", pincode: "" });

  // Auto-check pincode serviceability whenever the effective pincode changes
  const effectivePincode = selectedAddrId
    ? (addresses.find((a) => a.address_id === selectedAddrId)?.pincode || "")
    : newAddr.pincode;

  useEffect(() => {
    if (!effectivePincode || effectivePincode.length !== 6) {
      setPincodeCheck({ status: "idle", pincode: effectivePincode });
      return;
    }
    let ignore = false;
    setPincodeCheck({ status: "checking", pincode: effectivePincode });
    api.get(`/shipping/pincode/${effectivePincode}`).then((r) => {
      if (ignore) return;
      setPincodeCheck({
        status: r.data.serviceable ? "ok" : "not_serviceable",
        pincode: effectivePincode,
        city: r.data?.details?.postal_code?.district,
      });
    }).catch(() => {
      if (!ignore) setPincodeCheck({ status: "unknown", pincode: effectivePincode });
    });
    return () => { ignore = true; };
  }, [effectivePincode]);

  const shipping = subtotal >= 499 || subtotal === 0 ? 0 : 49;
  const total = subtotal + shipping;

  useEffect(() => {
    if (loading || !hydrated) return;
    if (!user) { navigate("/signin?redirect=/checkout"); return; }
    if (items.length === 0) { navigate("/cart"); return; }
    api.get("/addresses").then((r) => {
      setAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) setSelectedAddrId(def.address_id);
    });
  }, [user, loading, hydrated, items.length, navigate]);

  const placeOrder = async () => {
    if (submitting) return;

    let addressToUse;
    if (selectedAddrId) {
      addressToUse = addresses.find((a) => a.address_id === selectedAddrId);
    } else {
      // validate new address
      if (!newAddr.full_name || !newAddr.phone || !newAddr.line1 || !newAddr.city || !newAddr.state || !newAddr.pincode) {
        toast.error("Please fill all address fields");
        return;
      }
      try {
        const r = await api.post("/addresses", { ...newAddr, is_default: addresses.length === 0 });
        addressToUse = r.data;
      } catch (e) {
        toast.error("Failed to save address"); return;
      }
    }

    if (pincodeCheck.status === "not_serviceable") {
      toast.error("This pincode is not serviceable by Delhivery. Please choose another address.");
      return;
    }

    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price,
          quantity: i.quantity, image_url: i.image_url,
        })),
        address: addressToUse,
        payment_method: payment,
        subtotal, shipping, total,
      });
      const order = r.data.order;
      clear();
      if (payment === "phonepe") {
        // Kick off real PhonePe initiation → redirect to PhonePe pay page
        try {
          const init = await api.post("/payments/phonepe/initiate", { order_id: order.order_id });
          if (init.data?.redirect_url) {
            window.location.href = init.data.redirect_url;
            return;
          }
        } catch (e) {
          toast.error("Payment initiation failed. Please try again or use COD.");
        }
      }
      navigate(`/order-success/${order.order_id}`);
    } catch (e) {
      toast.error("Order failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="checkout-page">
      <h1 className="font-serif text-5xl text-[#0A192F] mb-10">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-8">
          <section className="bg-white rounded-2xl p-6">
            <h2 className="font-serif text-2xl text-[#0A192F] mb-4">Delivery Address</h2>
            {addresses.length > 0 ? (
              <RadioGroup value={selectedAddrId || ""} onValueChange={setSelectedAddrId} className="space-y-3">
                {addresses.map((a) => (
                  <label key={a.address_id} className="flex items-start gap-3 p-4 border border-[#0A192F]/10 rounded-xl cursor-pointer hover:border-[#E65A28]">
                    <RadioGroupItem value={a.address_id} data-testid={`addr-radio-${a.address_id}`} />
                    <div className="text-sm">
                      <p className="font-semibold text-[#0A192F]">{a.full_name} • {a.phone}</p>
                      <p className="text-[#666] mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} - {a.pincode}</p>
                    </div>
                  </label>
                ))}
                <label className="flex items-center gap-3 p-4 border border-dashed border-[#0A192F]/20 rounded-xl cursor-pointer">
                  <RadioGroupItem value="" data-testid="addr-radio-new" />
                  <span className="text-sm text-[#0A192F]">+ Add a new address</span>
                </label>
              </RadioGroup>
            ) : null}

            {(!selectedAddrId) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["full_name","Full name","md:col-span-2"],
                  ["phone","Phone",""],
                  ["pincode","Pincode",""],
                  ["line1","Address line 1","md:col-span-2"],
                  ["line2","Address line 2 (optional)","md:col-span-2"],
                  ["city","City",""],
                  ["state","State",""],
                ].map(([k,l,cls]) => (
                  <div key={k} className={cls}>
                    <Label className="text-xs uppercase tracking-widest text-[#666]">{l}</Label>
                    <Input
                      value={newAddr[k]}
                      onChange={(e) => setNewAddr({ ...newAddr, [k]: e.target.value })}
                      className="mt-1 bg-[#F9F6F0]"
                      data-testid={`addr-input-${k}`}
                    />
                  </div>
                ))}
              </div>
            )}
            {pincodeCheck.status === "ok" && (
              <p className="mt-3 text-xs text-green-700" data-testid="pincode-ok">✓ We deliver to {pincodeCheck.city || "your pincode"} via Delhivery.</p>
            )}
            {pincodeCheck.status === "not_serviceable" && (
              <p className="mt-3 text-xs text-red-600" data-testid="pincode-bad">✗ We don&apos;t currently deliver to this pincode. Please try another.</p>
            )}
            {pincodeCheck.status === "checking" && (
              <p className="mt-3 text-xs text-[#666]">Checking pincode serviceability...</p>
            )}
          </section>

          <section className="bg-white rounded-2xl p-6">
            <h2 className="font-serif text-2xl text-[#0A192F] mb-4">Payment Method</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
              <label className="flex items-start gap-3 p-4 border border-[#0A192F]/10 rounded-xl cursor-pointer hover:border-[#E65A28]">
                <RadioGroupItem value="phonepe" data-testid="pay-phonepe" />
                <div>
                  <p className="font-semibold text-[#0A192F]">PhonePe Business</p>
                  <p className="text-xs text-[#666] mt-1">UPI, cards, netbanking. Secure payment powered by PhonePe.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-4 border border-[#0A192F]/10 rounded-xl cursor-pointer hover:border-[#E65A28]">
                <RadioGroupItem value="cod" data-testid="pay-cod" />
                <div>
                  <p className="font-semibold text-[#0A192F]">Cash on Delivery</p>
                  <p className="text-xs text-[#666] mt-1">Pay in cash when your order arrives.</p>
                </div>
              </label>
            </RadioGroup>
          </section>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-white rounded-2xl p-6 sticky top-32" data-testid="checkout-summary">
            <h3 className="font-serif text-2xl text-[#0A192F] mb-4">Order Summary</h3>
            <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 text-sm">
                  <img src={abs(i.image_url)} alt="" className="h-12 w-12 rounded object-contain bg-[#F4EBE1] p-1" />
                  <div className="flex-1">
                    <p className="text-[#0A192F] font-medium leading-tight">{i.name}</p>
                    <p className="text-[#666]">Qty {i.quantity}</p>
                  </div>
                  <p className="font-medium">₹{(i.price * i.quantity).toFixed(0)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#0A192F]/10 mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#666]">Subtotal</span><span>₹{subtotal.toFixed(0)}</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Shipping (Delhivery)</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
              <div className="border-t border-[#0A192F]/10 pt-3 flex justify-between text-lg font-semibold">
                <span>Total</span><span>₹{total.toFixed(0)}</span>
              </div>
            </div>
            <button
              onClick={placeOrder}
              disabled={submitting}
              className="mt-6 w-full bg-[#E65A28] text-white py-4 rounded-full font-semibold hover:-translate-y-0.5 transition-transform disabled:opacity-50"
              data-testid="place-order-btn"
            >
              {submitting ? "Placing..." : `Place Order • ₹${total.toFixed(0)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
